import router from "./index.js";
import store from "@/store"; // 导入vuex store
import api from "@/api/index";
import { resolveLoginRedirect } from "@/utils/login-redirect";

/**
 * 已登录用户访问登录页时回跳到原页面
 * 站内地址交给 vue-router 处理：应用启动阶段 window.location.replace 只改 hash 不触发渲染，
 * 会导致地址栏已是券页、页面仍停留在登录页；跨域地址（tushare.pro）才整页跳转
 */
function redirectAfterLogin(to, next) {
  const target = resolveLoginRedirect(to.query.redirect);
  let url;
  try {
    url = new URL(target);
  } catch (e) {
    next();
    return;
  }

  if (url.origin === window.location.origin) {
    const location = url.hash ? url.hash.slice(1) : `${url.pathname}${url.search}`;
    // redirect 指回登录页时回首页，避免无限回跳
    next(location.split("?")[0] === "/login" ? "/" : location);
    return;
  }

  window.location.replace(target);
}

// 路由守卫
router.beforeEach((to, from, next) => {
  // 获取用户信息
  const userInfo = store.state.userinfo;
  const isAuthenticated = userInfo && userInfo.id;

  // ===== OAuth 独立路由体系（与主站逻辑隔离，互不影响） =====
  if (to.path.startsWith("/oauth")) {
    // 已登录访问 oauth 登录页：直接跳转授权页，延续 OAuth 授权流程
    if (to.path === "/oauth/login" && isAuthenticated) {
      const { redirect, ...rest } = to.query;
      if (redirect && redirect.startsWith("/oauth/")) {
        next(redirect);
      } else {
        next({ path: "/oauth/authorize", query: rest });
      }
      return;
    }

    // 授权页需要登录态：未登录跳 oauth 登录页，并携带回跳地址避免丢失 OAuth 上下文
    if (
      to.matched.some((record) => record.meta.requiresAuth) &&
      !isAuthenticated
    ) {
      next({
        path: "/oauth/login",
        query: { ...to.query, redirect: to.fullPath },
      });
      return;
    }

    next();
    return;
  }

  // 已登录用户访问登录页：按 redirect 回跳到触发登录的原页面（如优惠券领取页），
  // 保留用户操作上下文；回跳地址经白名单校验，参数缺失/非法时回站点根
  if (to.path === "/login" && isAuthenticated) {
    api
      // 校验会话的请求跳过全局 40201 跳转：否则接口返回未登录时会再触发一次
      // global 跳登录，覆盖掉本次携带 redirect 的导航（回跳参数丢失）
      .getUserInfo({ skipAuthRedirect: true })
      .then((res) => {
        const { code } = res;
        if (code === 0) {
          redirectAfterLogin(to, next);
        } else {
          // 会话已失效：清掉本地登录态，留在登录页重新登录
          store.dispatch("clearUserinfo");
          next();
        }
      })
      .catch(() => {
        // 校验接口异常（网络/超时）时清掉本地登录态并留在登录页，避免回跳后死循环
        store.dispatch("clearUserinfo");
        next();
      });
    return;
  }

  // 检查是否需要验证用户信息
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // 没有用户信息，跳转到登录页
      next({
        path: "/login",
        // 保存目标页 fullPath，登录成功后由 resolveLoginRedirect 拼回 hash 地址原路返回
        query: { redirect: to.fullPath },
      });
    } else {
      // 有用户信息，允许访问
      next();
    }
  } else {
    if (to?.query?.reg) {
      // 不需要验证的页面，允许访问
      window.sessionStorage.setItem("reg", to?.query?.reg);
    }
    next();
  }
});

export default router;
